import { useCallback, useRef, type MouseEvent, type ReactNode } from 'react'

import { IconMagicSolid, Modal, TransparentIconButton } from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import {
  GlobalCommandModalBodyContentStyled,
  GlobalCommandModalBodyStyled,
  GlobalCommandModalCloseAreaStyled,
  GlobalCommandModalFooterContentStyled,
  GlobalCommandModalFooterStyled,
  GlobalCommandModalHeaderContentStyled,
  GlobalCommandModalHeaderStyled,
  GlobalCommandModalLogoStyled,
  GlobalCommandModalMainStyled,
  GlobalCommandModalRowStyled,
  GlobalCommandModalShellStyled,
  GlobalCommandModalTitleStyled,
} from './styles/GlobalCommandModalLayoutStyled'

const INTERACTIVE_TARGET_SELECTOR = [
  'form',
  'button',
  'a',
  'input',
  'textarea',
  'select',
  'label',
  '[role="button"]',
  '[role="option"]',
  '[role="combobox"]',
  '[role="listbox"]',
  '[role="menuitem"]',
].join(', ')

const dismissPopovers = () => document.body.click()

interface IGlobalCommandModalProps {
  isOpen: boolean
  onClose: () => void
  onEntered?: () => void
  children: ReactNode
  isConversation?: boolean
  title?: ReactNode
  headerAction?: ReactNode
  footer?: ReactNode
  sidePanel?: ReactNode
}

export const GlobalCommandModal = ({
  isOpen,
  onClose,
  onEntered,
  children,
  isConversation = false,
  title,
  headerAction,
  footer,
  sidePanel,
}: IGlobalCommandModalProps) => {
  const intl = useProcurifyIntl()
  const shellRef = useRef<HTMLDivElement>(null)

  // Clicking anywhere non-interactive inside the modal returns focus to the
  // text field so the user can keep typing after clicking away.
  const handleShellClick = useCallback((event: MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement
    if (target.closest(INTERACTIVE_TARGET_SELECTOR)) return
    if (window.getSelection()?.toString()) return
    const field =
      shellRef.current?.querySelector<HTMLElement>('textarea') ??
      shellRef.current?.querySelector<HTMLElement>('input')
    field?.focus()
  }, [])

  return (
    <Modal
      aria-label={intl.formatMessage(messages.modalAriaLabel)}
      maxWidth={false}
      open={isOpen}
      onClose={onClose}
      PaperProps={{ elevation: 0 }}
      disableEscapeKeyDown={isConversation}
      TransitionProps={{ onEnter: dismissPopovers, onEntered }}
    >
      <Modal.Card cycleFocus>
        <GlobalCommandModalShellStyled
          ref={shellRef}
          $isConversation={isConversation}
          onClick={handleShellClick}
        >
          {!isConversation && (
            <GlobalCommandModalCloseAreaStyled>
              <TransparentIconButton
                aria-label={intl.formatMessage(messages.closeButtonAriaLabel)}
                icon='times'
                analyticsId='global-search-close-button'
                onClick={onClose}
              />
            </GlobalCommandModalCloseAreaStyled>
          )}

          {isConversation && (
            <GlobalCommandModalHeaderStyled>
              <GlobalCommandModalHeaderContentStyled>
                <GlobalCommandModalLogoStyled>
                  <IconMagicSolid size={16} color='primary' block />
                </GlobalCommandModalLogoStyled>
                {title && (
                  <GlobalCommandModalTitleStyled>
                    {title}
                  </GlobalCommandModalTitleStyled>
                )}
              </GlobalCommandModalHeaderContentStyled>
              {headerAction}
              <TransparentIconButton
                aria-label={intl.formatMessage(messages.closeButtonAriaLabel)}
                icon='times'
                analyticsId='global-search-close-button'
                onClick={onClose}
              />
            </GlobalCommandModalHeaderStyled>
          )}

          <GlobalCommandModalRowStyled>
            <GlobalCommandModalMainStyled>
              <GlobalCommandModalBodyStyled $isConversation={isConversation}>
                <GlobalCommandModalBodyContentStyled
                  $isConversation={isConversation}
                >
                  {children}
                </GlobalCommandModalBodyContentStyled>
              </GlobalCommandModalBodyStyled>

              {isConversation && footer && (
                <GlobalCommandModalFooterStyled>
                  <GlobalCommandModalFooterContentStyled>
                    {footer}
                  </GlobalCommandModalFooterContentStyled>
                </GlobalCommandModalFooterStyled>
              )}
            </GlobalCommandModalMainStyled>

            {isConversation && sidePanel}
          </GlobalCommandModalRowStyled>
        </GlobalCommandModalShellStyled>
      </Modal.Card>
    </Modal>
  )
}
